import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Camera, RotateCcw, Eye, Pause, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import faceAPIService, { LivenessDetector } from '../services/faceAPIService';

/**
 * Video Enrollment Component with Automatic Liveness Detection
 * 
 * Uses face-api.js for:
 * - Real-time face detection
 * - Automatic blink detection (Eye Aspect Ratio)
 * - Automatic pause detection (face position tracking)
 * - Head rotation tracking
 * 
 * Flow:
 * 1. Load models and start camera
 * 2. User rotates head (left → front → right)
 * 3. Random challenges: "Blink twice" / "Hold still"
 * 4. Auto-detection verifies challenges without button taps
 * 5. Frames extracted at key angles for enrollment
 */

const ENROLLMENT_STATES = {
  LOADING_MODELS: 'loading_models',
  READY: 'ready',
  RECORDING: 'recording',
  CHALLENGE_BLINK: 'challenge_blink',
  CHALLENGE_PAUSE: 'challenge_pause',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  ERROR: 'error',
};

const ROTATION_STAGES = {
  START: 'start',
  TURN_LEFT: 'turn_left',
  CENTER: 'center',
  TURN_RIGHT: 'turn_right',
  DONE: 'done',
};

function VideoEnrollment({ onComplete, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);
  const livenessDetector = useRef(new LivenessDetector());
  
  const [state, setState] = useState(ENROLLMENT_STATES.LOADING_MODELS);
  const [rotationStage, setRotationStage] = useState(ROTATION_STAGES.START);
  const [progress, setProgress] = useState(0);
  const [instruction, setInstruction] = useState('Loading face detection...');
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [faceDetected, setFaceDetected] = useState(false);
  const [challengeStarted, setChallengeStarted] = useState({ blink: false, pause: false });
  const [blinkProgress, setBlinkProgress] = useState(0);
  const [pauseProgress, setPauseProgress] = useState(0);
  const [error, setError] = useState(null);
  const [currentRotation, setCurrentRotation] = useState(0);
  const [debugInfo, setDebugInfo] = useState({ ear: 0, rotation: 0 });

  // Challenge timing (randomized)
  const challengeTimingRef = useRef({
    blinkAt: Math.random() * 0.2 + 0.15, // 15-35% through
    pauseAt: Math.random() * 0.2 + 0.55, // 55-75% through
  });

  // Track challenge completion
  const challengesRef = useRef({
    blinkRequired: 2,
    blinkCompleted: 0,
    pauseRequired: true,
    pauseCompleted: false,
  });

  // Load face-api.js models
  useEffect(() => {
    const loadModels = async () => {
      setState(ENROLLMENT_STATES.LOADING_MODELS);
      setInstruction('Loading face detection models...');
      
      const loaded = await faceAPIService.loadModels();
      
      if (loaded) {
        await startCamera();
        setState(ENROLLMENT_STATES.READY);
        setInstruction('Position your face in the oval');
      } else {
        setError('Failed to load face detection. Please refresh and try again.');
        setState(ENROLLMENT_STATES.ERROR);
      }
    };

    loadModels();
    
    return () => {
      stopCamera();
    };
  }, []);

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      setError('Camera access denied. Please enable camera permissions.');
      setState(ENROLLMENT_STATES.ERROR);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  // Capture frame from video
  const captureFrame = useCallback((angle) => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Mirror the image (selfie mode)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    return {
      data: canvas.toDataURL('image/jpeg', 0.9),
      angle,
      timestamp: Date.now(),
    };
  }, []);

  // Draw face detection overlay
  const drawOverlay = useCallback((detection) => {
    const canvas = overlayCanvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (detection && detection.detection) {
      const box = detection.detection.box;
      
      // Mirror coordinates for selfie view
      const mirroredX = canvas.width - box.x - box.width;
      
      // Draw face box
      ctx.strokeStyle = faceDetected ? '#22c55e' : '#ef4444';
      ctx.lineWidth = 3;
      ctx.strokeRect(mirroredX, box.y, box.width, box.height);
    }
  }, [faceDetected]);

  // Main detection loop
  const runDetection = useCallback(async () => {
    if (!videoRef.current || videoRef.current.readyState !== 4) {
      animationRef.current = requestAnimationFrame(runDetection);
      return;
    }

    if (state !== ENROLLMENT_STATES.RECORDING && 
        state !== ENROLLMENT_STATES.CHALLENGE_BLINK &&
        state !== ENROLLMENT_STATES.CHALLENGE_PAUSE) {
      animationRef.current = requestAnimationFrame(runDetection);
      return;
    }

    // Detect face
    const detection = await faceAPIService.detectFace(videoRef.current);
    
    // Process with liveness detector
    const result = livenessDetector.current.processDetection(detection);
    
    setFaceDetected(result.faceDetected);
    setCurrentRotation(result.rotation);
    setDebugInfo({ ear: result.ear?.toFixed(3) || 0, rotation: result.rotation?.toFixed(1) || 0 });

    // Draw overlay
    drawOverlay(detection);

    if (!result.faceDetected) {
      setInstruction('Position your face in the frame');
      animationRef.current = requestAnimationFrame(runDetection);
      return;
    }

    // Update progress based on rotation
    const absRotation = Math.abs(result.rotation);
    let newProgress = progress;

    if (rotationStage === ROTATION_STAGES.START) {
      if (absRotation < 10) {
        newProgress = Math.min(10, progress + 1);
        if (newProgress >= 10) {
          setRotationStage(ROTATION_STAGES.TURN_LEFT);
          setInstruction('← Slowly turn your head LEFT');
        }
      }
    } else if (rotationStage === ROTATION_STAGES.TURN_LEFT) {
      if (result.rotation < -20) {
        newProgress = Math.min(35, progress + 1);
        // Capture left profile
        if (progress >= 25 && capturedFrames.length === 0) {
          const frame = captureFrame('left_profile');
          if (frame) setCapturedFrames(prev => [...prev, frame]);
        }
      }
    } else if (rotationStage === ROTATION_STAGES.CENTER) {
      if (absRotation < 15) {
        newProgress = Math.min(65, progress + 1);
        // Capture front
        if (progress >= 50 && capturedFrames.length === 1) {
          const frame = captureFrame('front');
          if (frame) setCapturedFrames(prev => [...prev, frame]);
        }
      }
    } else if (rotationStage === ROTATION_STAGES.TURN_RIGHT) {
      if (result.rotation > 20) {
        newProgress = Math.min(100, progress + 1);
        // Capture right profile
        if (progress >= 85 && capturedFrames.length === 2) {
          const frame = captureFrame('right_profile');
          if (frame) setCapturedFrames(prev => [...prev, frame]);
        }
      }
    }

    setProgress(newProgress);

    // Check challenge triggers
    const progressRatio = newProgress / 100;
    const timing = challengeTimingRef.current;
    const challenges = challengesRef.current;

    // Trigger blink challenge
    if (progressRatio >= timing.blinkAt && 
        !challengeStarted.blink && 
        state === ENROLLMENT_STATES.RECORDING) {
      setChallengeStarted(prev => ({ ...prev, blink: true }));
      setState(ENROLLMENT_STATES.CHALLENGE_BLINK);
      setInstruction('👁️ BLINK TWICE');
      toast.info('Blink twice - we\'ll detect it automatically!');
      livenessDetector.current.blinkCount = 0; // Reset blink counter
    }

    // Handle blink challenge
    if (state === ENROLLMENT_STATES.CHALLENGE_BLINK) {
      if (result.blinked) {
        challenges.blinkCompleted++;
        setBlinkProgress(challenges.blinkCompleted);
        
        if (challenges.blinkCompleted >= challenges.blinkRequired) {
          toast.success('Blink challenge complete!');
          setState(ENROLLMENT_STATES.RECORDING);
          setRotationStage(ROTATION_STAGES.CENTER);
          setInstruction('Great! Now look straight at the camera');
        } else {
          setInstruction(`👁️ Blink ${challenges.blinkRequired - challenges.blinkCompleted} more time(s)`);
        }
      }
    }

    // Trigger pause challenge
    if (progressRatio >= timing.pauseAt && 
        !challengeStarted.pause && 
        challenges.blinkCompleted >= challenges.blinkRequired &&
        state === ENROLLMENT_STATES.RECORDING) {
      setChallengeStarted(prev => ({ ...prev, pause: true }));
      setState(ENROLLMENT_STATES.CHALLENGE_PAUSE);
      setInstruction('⏸️ HOLD STILL for 2 seconds');
      toast.info('Hold still - don\'t move!');
      livenessDetector.current.resetPause();
    }

    // Handle pause challenge
    if (state === ENROLLMENT_STATES.CHALLENGE_PAUSE) {
      const pauseDuration = livenessDetector.current.getPauseDuration();
      setPauseProgress(Math.min(100, (pauseDuration / 2000) * 100));
      
      if (result.isPaused || livenessDetector.current.isPauseComplete()) {
        challenges.pauseCompleted = true;
        toast.success('Pause challenge complete!');
        setState(ENROLLMENT_STATES.RECORDING);
        setRotationStage(ROTATION_STAGES.TURN_RIGHT);
        setInstruction('→ Now slowly turn your head RIGHT');
      }
    }

    // Update instructions during recording
    if (state === ENROLLMENT_STATES.RECORDING) {
      if (rotationStage === ROTATION_STAGES.START) {
        setInstruction('Look straight at the camera');
      } else if (rotationStage === ROTATION_STAGES.TURN_LEFT) {
        setInstruction('← Slowly turn your head LEFT');
      } else if (rotationStage === ROTATION_STAGES.CENTER) {
        setInstruction('Return to CENTER');
      } else if (rotationStage === ROTATION_STAGES.TURN_RIGHT) {
        setInstruction('→ Slowly turn your head RIGHT');
      }
    }

    // Check completion
    if (newProgress >= 95 && 
        challenges.blinkCompleted >= challenges.blinkRequired && 
        challenges.pauseCompleted &&
        capturedFrames.length >= 3) {
      completeEnrollment();
      return;
    }

    animationRef.current = requestAnimationFrame(runDetection);
  }, [state, progress, rotationStage, challengeStarted, capturedFrames, captureFrame, drawOverlay]);

  // Start enrollment
  const startEnrollment = () => {
    livenessDetector.current.reset();
    setCapturedFrames([]);
    setProgress(0);
    setBlinkProgress(0);
    setPauseProgress(0);
    setChallengeStarted({ blink: false, pause: false });
    challengesRef.current = {
      blinkRequired: 2,
      blinkCompleted: 0,
      pauseRequired: true,
      pauseCompleted: false,
    };
    // Randomize challenge timing
    challengeTimingRef.current = {
      blinkAt: Math.random() * 0.2 + 0.15,
      pauseAt: Math.random() * 0.2 + 0.55,
    };
    setRotationStage(ROTATION_STAGES.START);
    setState(ENROLLMENT_STATES.RECORDING);
    setInstruction('Look straight at the camera');
    
    animationRef.current = requestAnimationFrame(runDetection);
  };

  // Complete enrollment
  const completeEnrollment = async () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setState(ENROLLMENT_STATES.PROCESSING);
    setInstruction('Processing enrollment...');

    try {
      // Capture any remaining frames
      if (capturedFrames.length < 5) {
        const extraAngles = ['front_2', 'center_2'];
        for (let i = capturedFrames.length; i < 5; i++) {
          const frame = captureFrame(extraAngles[i - 3] || `angle_${i}`);
          if (frame) {
            setCapturedFrames(prev => [...prev, frame]);
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      setState(ENROLLMENT_STATES.COMPLETE);
      setInstruction('Enrollment complete!');
      toast.success('Face enrollment successful!');

      setTimeout(() => {
        if (onComplete) {
          onComplete(capturedFrames);
        }
      }, 1000);

    } catch (err) {
      setError(err.message || 'Enrollment failed');
      setState(ENROLLMENT_STATES.ERROR);
    }
  };

  // Get stage indicator color
  const getStageColor = (stage) => {
    const stageOrder = [ROTATION_STAGES.TURN_LEFT, ROTATION_STAGES.CENTER, ROTATION_STAGES.TURN_RIGHT];
    const currentIndex = stageOrder.indexOf(rotationStage);
    const stageIndex = stageOrder.indexOf(stage);
    
    if (rotationStage === stage) return 'bg-primary text-white';
    if (stageIndex < currentIndex) return 'bg-green-500 text-white';
    return 'bg-gray-200 text-gray-500';
  };

  return (
    <div className="flex flex-col items-center w-full max-w-md mx-auto p-4">
      {/* Header */}
      <div className="text-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Face Enrollment</h2>
        <p className="text-sm text-gray-500 mt-1">
          {state === ENROLLMENT_STATES.LOADING_MODELS ? 'Loading AI...' : 'Automatic liveness detection'}
        </p>
      </div>

      {/* Video Container */}
      <div className="relative w-full aspect-[3/4] bg-black rounded-2xl overflow-hidden mb-4">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
        <canvas ref={canvasRef} className="hidden" />
        <canvas 
          ref={overlayCanvasRef} 
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ transform: 'scaleX(-1)' }}
        />

        {/* Face guide overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div 
            className={`w-48 h-64 border-4 rounded-full transition-colors duration-300 ${
              faceDetected ? 'border-green-400' : 'border-white/50'
            }`}
          />
        </div>

        {/* Loading overlay */}
        {state === ENROLLMENT_STATES.LOADING_MODELS && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="text-center text-white">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-2" />
              <p>Loading face detection...</p>
            </div>
          </div>
        )}

        {/* Rotation guide arrows */}
        {(state === ENROLLMENT_STATES.RECORDING || 
          state === ENROLLMENT_STATES.CHALLENGE_BLINK ||
          state === ENROLLMENT_STATES.CHALLENGE_PAUSE) && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 pointer-events-none">
            <ChevronLeft 
              className={`w-10 h-10 transition-colors ${
                rotationStage === ROTATION_STAGES.TURN_LEFT 
                  ? 'text-yellow-400 animate-pulse' 
                  : 'text-white/30'
              }`}
            />
            <ChevronRight 
              className={`w-10 h-10 transition-colors ${
                rotationStage === ROTATION_STAGES.TURN_RIGHT 
                  ? 'text-yellow-400 animate-pulse' 
                  : 'text-white/30'
              }`}
            />
          </div>
        )}

        {/* Blink challenge overlay */}
        {state === ENROLLMENT_STATES.CHALLENGE_BLINK && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 text-center shadow-lg">
              <Eye className="w-12 h-12 mx-auto text-primary mb-2 animate-pulse" />
              <p className="text-lg font-semibold">Blink Twice</p>
              <p className="text-sm text-gray-500 mb-2">
                {challengesRef.current.blinkRequired - blinkProgress} remaining
              </p>
              <div className="flex gap-2 justify-center">
                {[...Array(challengesRef.current.blinkRequired)].map((_, i) => (
                  <div 
                    key={`blink-${i}`}
                    className={`w-4 h-4 rounded-full transition-colors ${
                      i < blinkProgress ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Pause challenge overlay */}
        {state === ENROLLMENT_STATES.CHALLENGE_PAUSE && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 text-center shadow-lg">
              <Pause className="w-12 h-12 mx-auto text-primary mb-2" />
              <p className="text-lg font-semibold">Hold Still</p>
              <p className="text-sm text-gray-500 mb-2">Don't move for 2 seconds</p>
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden mx-auto">
                <div 
                  className="h-full bg-blue-500 transition-all duration-100"
                  style={{ width: `${pauseProgress}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Progress bar */}
        <div className="absolute bottom-0 inset-x-0 h-2 bg-black/30">
          <div 
            className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-150"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Captured frames indicator */}
        <div className="absolute top-4 right-4 flex gap-1">
          {[...Array(5)].map((_, i) => (
            <div 
              key={`frame-${i}`}
              className={`w-3 h-3 rounded-full transition-colors ${
                i < capturedFrames.length ? 'bg-green-500' : 'bg-white/30'
              }`}
            />
          ))}
        </div>

        {/* Face detected indicator */}
        <div className="absolute top-4 left-4">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            faceDetected ? 'bg-green-500/80 text-white' : 'bg-red-500/80 text-white'
          }`}>
            {faceDetected ? '✓ Face detected' : '✗ No face'}
          </div>
        </div>
      </div>

      {/* Stage indicators */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <div className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${getStageColor(ROTATION_STAGES.TURN_LEFT)}`}>
          ← Left
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${getStageColor(ROTATION_STAGES.CENTER)}`}>
          Center
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${getStageColor(ROTATION_STAGES.TURN_RIGHT)}`}>
          Right →
        </div>
      </div>

      {/* Challenge status */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <div className={`flex items-center gap-1 text-sm ${
          blinkProgress >= 2 ? 'text-green-600' : 'text-gray-400'
        }`}>
          <Eye className="w-4 h-4" />
          <span>Blink {blinkProgress >= 2 ? '✓' : `${blinkProgress}/2`}</span>
        </div>
        <div className={`flex items-center gap-1 text-sm ${
          challengesRef.current.pauseCompleted ? 'text-green-600' : 'text-gray-400'
        }`}>
          <Pause className="w-4 h-4" />
          <span>Pause {challengesRef.current.pauseCompleted ? '✓' : ''}</span>
        </div>
      </div>

      {/* Instruction text */}
      <div className="text-center mb-6">
        <p className={`text-lg font-medium ${
          state === ENROLLMENT_STATES.ERROR ? 'text-red-600' : 'text-gray-800'
        }`}>
          {instruction}
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-3 w-full">
        {state === ENROLLMENT_STATES.READY && (
          <button
            onClick={startEnrollment}
            className="w-full py-3 px-6 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
            data-testid="start-enrollment-btn"
          >
            <Camera className="w-5 h-5" />
            Start Face Scan
          </button>
        )}

        {state === ENROLLMENT_STATES.COMPLETE && (
          <div className="flex items-center justify-center gap-2 text-green-600">
            <CheckCircle className="w-6 h-6" />
            <span className="font-medium">Enrollment Complete!</span>
          </div>
        )}

        {state === ENROLLMENT_STATES.ERROR && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 text-red-600">
              <AlertCircle className="w-6 h-6" />
              <span className="font-medium">{error}</span>
            </div>
            <button
              onClick={() => {
                setError(null);
                setState(ENROLLMENT_STATES.READY);
              }}
              className="w-full py-3 px-6 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              Try Again
            </button>
          </div>
        )}

        {(state === ENROLLMENT_STATES.READY || 
          state === ENROLLMENT_STATES.LOADING_MODELS) && onCancel && (
          <button
            onClick={onCancel}
            className="w-full py-2 px-6 text-gray-500 rounded-xl font-medium hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Debug info (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs text-gray-600 w-full font-mono">
          <p>State: {state} | Stage: {rotationStage}</p>
          <p>Progress: {progress.toFixed(0)}% | Frames: {capturedFrames.length}/5</p>
          <p>EAR: {debugInfo.ear} | Rotation: {debugInfo.rotation}°</p>
          <p>Blinks: {blinkProgress} | Pause: {pauseProgress.toFixed(0)}%</p>
        </div>
      )}
    </div>
  );
}

export default VideoEnrollment;
